import { X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Common/button';
import { Input } from '@/components/Common/input';
import type { Organization, UserRole } from '@/types';
import { DEFAULT_PROFILE_FILTER, isCompleteDateInput, type AdminUserFilters } from '@/types/adminUsers';
import type { Profile } from '@/types/profiles';
import { UserRoles } from '@/types';

export interface AdminUsersFiltersProps {
  value: AdminUserFilters;
  onChange: (next: AdminUserFilters) => void;
  organizations: Organization[];
  /** All profiles (global + org-scoped); narrowed here when an organization is selected */
  profiles: Profile[];
  onReset: () => void;
}

const selectClassName = 'h-9 max-w-full rounded-md border border-border bg-background px-2 text-sm';

/**
 * Filter bar for the super-admin Users list. Controlled: every change is
 * reported through onChange and applied server-side by the parent.
 */
export const AdminUsersFilters: React.FC<AdminUsersFiltersProps> = ({
  value,
  onChange,
  organizations,
  profiles,
  onReset,
}) => {
  const { t } = useTranslation();

  const orgNames = useMemo(
    () => new Map(organizations.map((org): [string, string] => [org.id, org.name])),
    [organizations],
  );

  // With an org selected, only global profiles and that org's own profiles can match.
  const visibleProfiles = useMemo(
    () =>
      value.organizationId
        ? profiles.filter((p) => !p.organization_id || p.organization_id === value.organizationId)
        : profiles,
    [profiles, value.organizationId],
  );

  const hasActiveFilter = Object.values(value).some((v) => v !== '');

  const handleOrganizationChange = (organizationId: string) => {
    const selectedProfile = profiles.find((p) => p.id === value.profileId);
    const profileStillVisible =
      !organizationId ||
      !selectedProfile ||
      !selectedProfile.organization_id ||
      selectedProfile.organization_id === organizationId;
    onChange({ ...value, organizationId, profileId: profileStillVisible ? value.profileId : '' });
  };

  // Date inputs keep a local draft: typing a year emits partial values (0002-…, 0020-…),
  // and only a complete date (or a cleared field) is passed up and fetched.
  const [createdFromDraft, setCreatedFromDraft] = useState(value.createdFrom);
  const [createdToDraft, setCreatedToDraft] = useState(value.createdTo);
  useEffect(() => setCreatedFromDraft(value.createdFrom), [value.createdFrom]);
  useEffect(() => setCreatedToDraft(value.createdTo), [value.createdTo]);

  const handleCreatedFromChange = (createdFrom: string) => {
    setCreatedFromDraft(createdFrom);
    if (createdFrom === '' || isCompleteDateInput(createdFrom)) onChange({ ...value, createdFrom });
  };

  const handleCreatedToChange = (createdTo: string) => {
    setCreatedToDraft(createdTo);
    if (createdTo === '' || isCompleteDateInput(createdTo)) onChange({ ...value, createdTo });
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <select
        value={value.organizationId}
        onChange={(e) => handleOrganizationChange(e.target.value)}
        className={selectClassName}
        aria-label={t('admin.users.table.organization', 'Organization')}
      >
        <option value="">{t('admin.users.filters.allOrganizations', 'All organizations')}</option>
        {organizations.map((org) => (
          <option key={org.id} value={org.id}>
            {org.name}
          </option>
        ))}
      </select>
      <select
        value={value.role}
        onChange={(e) => onChange({ ...value, role: e.target.value as UserRole | '' })}
        className={selectClassName}
        aria-label={t('admin.users.table.role', 'Role')}
      >
        <option value="">{t('admin.users.filters.allRoles', 'All roles')}</option>
        <option value={UserRoles.SuperAdmin}>{t('admin.users.roleSuperAdmin', 'Super Admin')}</option>
        <option value={UserRoles.Admin}>{t('admin.users.roleAdmin', 'Admin')}</option>
        <option value={UserRoles.User}>{t('admin.users.roleUser', 'User')}</option>
        <option value={UserRoles.Demo}>{t('admin.users.roleDemo', 'Demo')}</option>
      </select>
      <select
        value={value.profileId}
        onChange={(e) => onChange({ ...value, profileId: e.target.value })}
        className={selectClassName}
        aria-label={t('admin.users.table.profile', 'Profile')}
      >
        <option value="">{t('admin.users.filters.allProfiles', 'All profiles')}</option>
        <option value={DEFAULT_PROFILE_FILTER}>— {t('admin.users.profileDefault', 'Default (role)')} —</option>
        {visibleProfiles.map((p) => {
          // Org-scoped profiles from different orgs can share a name; show the org when not narrowed.
          const orgName = !value.organizationId && p.organization_id ? orgNames.get(p.organization_id) : undefined;
          return (
            <option key={p.id} value={p.id}>
              {orgName ? `${p.name} (${orgName})` : p.name}
            </option>
          );
        })}
      </select>
      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {t('admin.users.filters.createdFrom', 'Created from')}
        <Input
          type="date"
          value={createdFromDraft}
          max={value.createdTo || undefined}
          onChange={(e) => handleCreatedFromChange(e.target.value)}
          className="h-9 w-auto border-border px-2 text-foreground"
        />
      </label>
      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {t('admin.users.filters.createdTo', 'Created until')}
        <Input
          type="date"
          value={createdToDraft}
          min={value.createdFrom || undefined}
          onChange={(e) => handleCreatedToChange(e.target.value)}
          className="h-9 w-auto border-border px-2 text-foreground"
        />
      </label>
      {hasActiveFilter && (
        <Button variant="ghost" size="sm" onClick={onReset} className="ml-auto h-8">
          <X className="h-4 w-4 mr-1" aria-hidden="true" />
          {t('admin.users.filters.reset', 'Reset')}
        </Button>
      )}
    </div>
  );
};
